import { cn } from "@utils/tw"
import { useNav } from "@hooks/useNav"
import { Menu } from "@components/atoms/Menu"
import { useIsMobile } from "@hooks/useViewportDerived"
import { useDocumentArchive, useLatestNews, useSelectedDocument } from "@hooks/queries"
import { Typography } from "@components/atoms/Typography"
import { Button } from "@components/atoms/Button"
import { Swiper, SwiperSlide } from "@components/atoms/Swiper"
import { ComponentProps, ComponentPropsWithoutRef, forwardRef, useEffect, useState } from "react"
import { Card } from "./Cards"
import { useMeasure } from "react-use"
import { Link } from "eddev/routing"
import { ARROW_RIGHT } from "@components/atoms/Icons"
import { VSpace } from "@components/layout/Spacer"
import { AnimatePresence, motion } from "framer-motion"
import { twMerge } from "tailwind-merge"

const slideTime = 5000

export const Navigation = () => {
  const nav = useNav()
  const mobile = useIsMobile()
  const latestNews = useLatestNews({ count: 3 })
  const latestDocument = useDocumentArchive({ count: 1 })
  const selectedDocument = useSelectedDocument(undefined)
  const [swiperEl, setSwiperEl] = useState<any>()
  const [currentSlide, setCurrentSlide] = useState<number>(1)
  const [colSizeRef, colSize] = useMeasure<HTMLDivElement>()
  const [buttonSizeRef, buttonSize] = useMeasure<HTMLDivElement>()
  const isMobile = useIsMobile()

  function handleSwiperInit(swiper: any) {
    setSwiperEl(swiper)
  }

  useEffect(() => {
    if (!swiperEl) return
    const handleSlideChange = () => setCurrentSlide(swiperEl.realIndex + 1)
    swiperEl.on("slideChange", handleSlideChange)
    return () => swiperEl.off("slideChange", handleSlideChange)
  }, [swiperEl])

  useEffect(() => {
    document.body.style.setProperty("--timer-move", `${colSize.width - buttonSize.width - 40}px`)
  }, [colSize.width])

  useEffect(() => {
    if (!nav.open) return
    const original = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
    return () => {
      document.documentElement.style.overflow = original
    }
  }, [nav.open])

  return (
    <AnimatePresence mode="wait">
      {nav.open && (
        <NavContainer initial="hidden" animate="visible" exit="hidden">
          <div className="!w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] mx-auto px-4 md:px-6 xxl:px-0">
            {mobile && (
              <>
                <div
                  className={twMerge(
                    "flex w-full",
                    "[&>ul]:flex [&>ul]:gap-4 [&>ul]:items-center [&>ul]:justify-start",
                    "[&>ul>li>a]:bg-white [&>ul>li>a]:type-label-s [&>ul>li>a]:py-2 [&>ul>li>a]:px-4 [&>ul>li>a]:rounded-md",
                    "[&>ul>li>a>svg]:hidden",
                  )}
                >
                  <Menu location="Quicklinks" />
                </div>
              </>
            )}
            <Nav>
              <Menu location="Main" showLevels={3} />
            </Nav>

            {isMobile ? (
              <>
                <div className="grid-container bg-white rounded-md">
                  <Link href="/newsroom" className="button">
                    Newsroom {ARROW_RIGHT}
                  </Link>
                </div>
                <VSpace initial={2} />
                <div className="grid-container bg-white rounded-md">
                  <Link href="/research-data/library/" className="button">
                    Document Library {ARROW_RIGHT}
                  </Link>
                </div>
                <VSpace initial={8} />
              </>
            ) : (
              <>
                <SectionHighlight>
                  <div className="grid grid-cols-12 gap-4">
                    <FlexCol className="col-span-12 md:col-span-2">
                      <Typography as="span" variant="label-l" className="px-3">
                        Newsroom
                      </Typography>
                    </FlexCol>

                    <div className="col-span-12 md:col-span-7 lg:col-span-8 flex items-center">
                      <FlexSwiperWrapper>
                        {latestNews.data?.posts && (
                          <>
                            <style>{`
                            @keyframes pieAniAfter {
                              0% { transform: rotate(0deg); background: #5E5E5E; }
                              49.99% { transform: rotate(180deg); background: #5E5E5E; }
                              50% { transform: rotate(0deg); background: white; }
                              99.99% { transform: rotate(180deg); background: white; }
                              100% { transform: rotate(0deg); background: #5E5E5E; }
                            }

                            .newsroom-swiper {
                              overflow: visible !important;

                              .swiper-pagination {
                                position: absolute;
                                top: 52% !important;
                                bottom: auto !important;
                                left: auto !important;
                                right: 20px !important;
                                transform: translate(calc(var(--tw-grid-gutter) + var(--timer-move)), -8px);
                                margin: 0;
                                padding: 0;
                                display: block;
                                width: 16px;
                                height: 16px;

                                .swiper-pagination-bullet {
                                  display: none;
                                  position: absolute !important;
                                  left: 0;
                                  width: 12px;
                                  height: 12px;
                                  border: 1px solid #5E5E5E;
                                  box-shadow: inset 0 0 1px #5E5E5E;
                                  border-radius: 50%;
                                  background: transparent;
                                  background-image: linear-gradient(90deg, #5E5E5E 50%, white 50%);
                                  overflow: hidden;
                                }

                                .swiper-pagination-bullet-active {
                                  display: block;
                                }

                                .swiper-pagination-bullet-active:after {
                                  position: absolute;
                                  left: 50%;
                                  width: 50%;
                                  height: 100%;
                                  content: "";
                                  background: #5E5E5E;
                                  transform-origin: 0 50%;
                                  animation: pieAniAfter ${slideTime}ms linear infinite;
                                }
                              }
                            }
                          `}</style>
                            <Swiper
                              className="newsroom-swiper"
                              initialSlide={2}
                              onSwiper={handleSwiperInit}
                              slidesPerView={1}
                              autoplay={{
                                delay: slideTime,
                              }}
                              effect="fade"
                              fadeEffect={{
                                crossFade: true,
                              }}
                              loop
                              pagination
                              children={latestNews.data.posts.nodes.map((article, i) => (
                                <SwiperSlide
                                  key={i}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <Card type="navigation" {...article} latest={i === 0} />
                                </SwiperSlide>
                              ))}
                            />
                          </>
                        )}
                      </FlexSwiperWrapper>
                    </div>

                    <FlexCol className="col-span-12 md:col-span-3 lg:col-span-2" ref={colSizeRef}>
                      {swiperEl && (
                        <Counter>
                          {currentSlide} / {swiperEl.slides?.length}
                        </Counter>
                      )}
                      <div ref={buttonSizeRef}>
                        <Button color="grey" href="/newsroom" size="medium" icon="CARET_RIGHT" iconStyle="right">
                          View all
                        </Button>
                      </div>
                    </FlexCol>
                  </div>
                </SectionHighlight>

                <SectionHighlight>
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <FlexCol className="col-span-12 md:col-span-2">
                      <Typography as="span" variant="label-l" className="px-3">
                        AAA Library
                      </Typography>
                    </FlexCol>
                    <div className="col-span-12 md:col-span-7 lg:col-span-8 flex items-center">
                      {selectedDocument.data?.siteSettings?.themeSiteSettings?.menuDocument && (
                        <Card
                          type="navigationDocument"
                          {...selectedDocument.data?.siteSettings?.themeSiteSettings?.menuDocument}
                        />
                      )}
                    </div>
                    <FlexCol className="col-span-12 md:col-span-3 lg:col-span-2 [&_.button]:grow-0">
                      <Button
                        color="grey"
                        href="/research-data/library/"
                        size="medium"
                        icon="CARET_RIGHT"
                        iconStyle="right"
                      >
                        View all
                      </Button>
                    </FlexCol>
                  </div>
                </SectionHighlight>
              </>
            )}
          </div>
        </NavContainer>
      )}
    </AnimatePresence>
  )
}

const SectionHighlight = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-full mx-auto mt-6 bg-white rounded-md [&>div]:py-2 lg:mt-3", className)} {...props} />
))
SectionHighlight.displayName = "SectionHighlight"

const FlexCol = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-3 gap-4 [&>div]:items-center last-of-type:justify-end", className)}
    {...props}
  />
))
FlexCol.displayName = "FlexCol"

// `all .7s cubic-bezier(.19,1,.22,1)` ($menu transition token) -> ease-menu / duration-700.
// Elsewhere below, `$movement` (`all .35s cubic-bezier(.19,1,.22,1)`) is inlined as
// `transition-all ease-movement duration-300`, matching the duration-300 rounding already
// established by Button.tsx's conversion of the same token.
const MENU_TRANSITION = "transition-all ease-menu duration-700"

const NavContainer = forwardRef<HTMLDivElement, ComponentProps<typeof motion.div>>(({ className, ...props }, ref) => (
  <motion.div
    ref={ref}
    className={cn(
      "w-full h-[100dvh] overflow-auto bg-light-grey fixed top-0 left-0 z-[2]",
      MENU_TRANSITION,
      "pt-[88px] md:pt-[96px] pb-5",
      "[&_div]:max-w-full",
      "[&_.button]:h-[48px] [&_.button]:flex [&_.button]:items-center [&_.button]:rounded-[6px]",
      "[&_.button]:transition-all [&_.button]:ease-movement [&_.button]:duration-300",
      "[&_.button]:justify-between [&_.button]:px-3 [&_.button]:m-3 [&_.button]:grow [&_.button]:bg-white [&_.button]:type-label-l",
      "[&_.button_svg]:size-[32px]",
      "[&_.button:hover]:bg-black [&_.button:hover]:text-white",
      "md:[&_.button]:h-auto",
      className,
    )}
    {...props}
  />
))
NavContainer.displayName = "NavContainer"

const Nav = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={twMerge(
      "w-full",
      "lg:[&>ul]:bg-white [&>ul]:rounded-md [&>ul]:grid [&>ul]:gap-6 [&>ul]:grid-cols-1 md:[&>ul]:grid-cols-2 lg:[&>ul]:grid-cols-4 [&>ul]:py-4",
      "[&>ul>li]:px-6 [&>ul>li]:py-2 lg:[&>ul>li]:border-r lg:[&>ul>li]:border-black/5 lg:[&>ul>li:last-of-type]:border-none",
      "[&>ul>li>a]:type-label-l [&>ul>li>a]:flex [&>ul>li>a]:gap-6 [&>ul>li>a]:justify-between [&>ul>li>a]:p-2 [&>ul>li>a]:rounded-md [&>ul>li>a]:transition-all [&>ul>li>a]:ease-out [&>ul>li>a]:duration-150",
      "hover:[&>ul>li>a]:bg-black hover:[&>ul>li>a]:text-white",
      "[&>ul>li>ul]:mt-4 lg:[&>ul>li>ul]:mt-6 [&>ul>li]:bg-white [&>ul>li]:py-6 md:[&>ul>li]:py-4 [&>ul>li]:rounded-md",
      "[&>ul>li_ul>li>a]:flex [&>ul>li_ul>li>a]:items-center [&>ul>li_ul>li>a]:justify-between [&>ul>li_ul>li>a]:type-label-s [&>ul>li_ul>li>a]:p-2 [&>ul>li_ul>li>a]:rounded-sm [&>ul>li_ul>li>a]:transition-all [&>ul>li_ul>li>a]:ease-out [&>ul>li_ul>li>a]:duration-150",
      "hover:[&>ul>li_ul>li>a]:bg-black hover:[&>ul>li_ul>li>a]:text-white",
      "[&>ul>li_ul>li>ul]:pl-4",
      "[&>ul>li_ul>li>ul>li>a]:flex-row-reverse [&>ul>li_ul>li>ul>li>a]:justify-end",
    )}
    {...props}
  />
))
Nav.displayName = "Nav"

const Counter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("type-label-caps w-[40px] flex justify-end whitespace-nowrap min-w-[40px]", className)}
    {...props}
  />
))
Counter.displayName = "Counter"

const FlexSwiperWrapper = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center w-full h-full justify-center",
        "[&>.swiper]:justify-center [&>.swiper]:w-full [&>.swiper]:flex",
        className,
      )}
      {...props}
    />
  ),
)
FlexSwiperWrapper.displayName = "FlexSwiperWrapper"
